/**
 * NodeIntrospectionTooltip — the formatted "what the agent was told" hover card
 * shown on each step row in the run summary.
 *
 * Structured mode (`view`): rendered from the run-pinned snapshot's node
 * config — title, instructions, and each input as a separate labeled field
 * (read-only echo of the node config panel: label + type tag, value box,
 * `$var` handle, helper text). Macros in values (`$EMAIL`, `$var.x`) render
 * verbatim.
 *
 * Fallback mode (`fallback`): title + muted prose split from the persisted
 * introspection string — used when the step's node isn't in the snapshot
 * (setup-flow inlined steps, deleted nodes) or for legacy runs.
 *
 * @module ui/pages/FlowDesigner/runs/NodeIntrospectionTooltip
 */
import React from 'react';
import { Text, Tooltip } from '@chatsdet/ui';
import type {
  IntrospectionField,
  IntrospectionView,
  ParsedNodeIntrospection,
} from './nodeIntrospection';

const SectionCaption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
    {children}
  </div>
);

const VarChip: React.FC<{ children: string }> = ({ children }) => (
  <code className="rounded bg-muted px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
    {children}
  </code>
);

const FieldBlock: React.FC<{ field: IntrospectionField }> = ({ field }) => {
  const helper = [
    field.description,
    field.options ? `Options: ${field.options.map((opt) => opt.label).join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <Text size="xs" weight="medium" className="leading-snug">
          {field.label}
          {field.optional ? (
            <span className="ml-1 font-normal text-muted-foreground">(optional)</span>
          ) : null}
        </Text>
        <span className="flex shrink-0 items-baseline gap-1.5">
          {field.type ? (
            <span className="text-[10px] text-muted-foreground">{field.type}</span>
          ) : null}
          <VarChip>{`$var.${field.name}`}</VarChip>
        </span>
      </div>
      {field.value !== null ? (
        <div className="mt-1 whitespace-pre-wrap break-words rounded border border-border bg-muted px-2 py-1 text-xs text-foreground">
          {field.value}
        </div>
      ) : (
        <div className="mt-1 rounded border border-dashed border-border px-2 py-1 text-xs italic text-muted-foreground">
          {field.example ? `e.g. ${field.example}` : 'No value provided'}
        </div>
      )}
      {helper ? (
        <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{helper}</div>
      ) : null}
    </div>
  );
};

const StructuredContent: React.FC<{ view: IntrospectionView }> = ({ view }) => (
  <div className="divide-y divide-border">
    <div className="px-3 py-2">
      <Text weight="medium" size="sm" className="leading-snug">
        {view.title}
      </Text>
      {view.instructions ? (
        <div className="mt-1.5 whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground">
          {view.instructions}
        </div>
      ) : null}
    </div>
    {view.fields.length > 0 ? (
      <div className="px-3 py-2">
        <SectionCaption>Inputs</SectionCaption>
        <div className="mt-1.5 space-y-2.5">
          {view.fields.map((field) => (
            <FieldBlock key={field.name} field={field} />
          ))}
        </div>
      </div>
    ) : null}
    {view.expectedOutputs.length > 0 ? (
      <div className="px-3 py-2">
        <SectionCaption>Outputs to capture</SectionCaption>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {view.expectedOutputs.map((name) => (
            <VarChip key={name}>{name}</VarChip>
          ))}
        </div>
      </div>
    ) : null}
  </div>
);

const FallbackContent: React.FC<ParsedNodeIntrospection> = ({ title, body }) => (
  <div className="px-3 py-2">
    {title ? (
      <Text weight="medium" size="sm" className="leading-snug">
        {title}
      </Text>
    ) : null}
    {body ? (
      <div
        className={`whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground ${title ? 'mt-1.5' : ''}`}
      >
        {body}
      </div>
    ) : null}
  </div>
);

export const NodeIntrospectionTooltip: React.FC<{
  view: IntrospectionView | null;
  fallback: ParsedNodeIntrospection | null;
}> = ({ view, fallback }) => (
  <Tooltip variant="light" className="max-w-md p-0 text-left">
    {view ? <StructuredContent view={view} /> : fallback ? <FallbackContent {...fallback} /> : null}
  </Tooltip>
);
